import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, ArrowLeft } from "@phosphor-icons/react";
import { myApi } from "../api";
import { gaEcommerce, cartToGaItems } from "@/lib/ga";
import { useCart } from "../contexts/CartContext";
import { validateCheckout } from "../../shared/checkoutValidation";
import { trackCheckout, cartSnapshot } from "../lib/checkoutTracker";
import AddressSection from "../components/shop/checkout/AddressSection";
import DeliveryMethodPicker from "../components/shop/checkout/DeliveryMethodPicker";
import PromoVouchersCredits from "../components/shop/checkout/PromoVouchersCredits";
import ObservationsBox from "../components/shop/checkout/ObservationsBox";
import CheckoutTotalsCard from "../components/shop/checkout/CheckoutTotalsCard";

/** Persist the user's checkout intent across navigations back from payment. */
const STORAGE_KEY = "my_checkout_state";

export default function MyCheckout() {
  const { cart, itemCount, currency, produtos, packs, total, refresh: refreshCart } = useCart();
  const navigate = useNavigate();

  // GA4: begin_checkout uma única vez por visita a esta página
  const gaBegun = useRef(false);
  useEffect(() => {
    if (!gaBegun.current && itemCount > 0) {
      gaBegun.current = true;
      gaEcommerce("begin_checkout", cartToGaItems(produtos, packs), total, currency);
      trackCheckout("checkout", cartSnapshot(produtos, packs));
    }
  }, [itemCount, produtos, packs, total, currency]);

  // Persisted selections
  const initial = (() => {
    try { return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
  })();
  const [shippingAddressId, setShippingAddressId] = useState(initial.shippingAddressId || "");
  const [billingAddressId, setBillingAddressId] = useState(initial.billingAddressId || "");
  const [deliveryMethod, setDeliveryMethod] = useState(initial.deliveryMethod || "");
  const [promoCode, setPromoCode] = useState(initial.promoCode || "");
  const [voucherAmount, setVoucherAmount] = useState(initial.voucherAmount || 0);
  const [creditsAmount, setCreditsAmount] = useState(initial.creditsAmount || 0);
  const [observations, setObservations] = useState(initial.observations || "");

  // Metadata
  const [countries, setCountries] = useState([]);
  const [deliveryMethods, setDeliveryMethods] = useState([]);

  // Cart shipping flags — apenas pedimos morada de envio se houver produtos
  // físicos (categoria LI/PM). Caso contrário (cursos, serviços, MP3, etc.)
  // assumimos automaticamente o tipo de transporte interno (LE — Levantamento
  // em evento) e escondemos as duas secções ao utilizador.
  const [cartFlags, setCartFlags] = useState({
    requiresShipping: true, // pessimist default: assume sim até saber
    physicalCount: 0,
    defaultDeliveryMethodId: "",
    loaded: false,
  });

  // Vales/Créditos disponíveis (loaded once on mount, used as fallback before verify runs)
  const [balances, setBalances] = useState({ vouchers: 0, credits: 0 });

  // Totals from /checkout/verify
  const [verifyData, setVerifyData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // Campo/secção em falta ("billing" | "delivery" | "shipping") — usado para
  // destacar a vermelho o bloco que impede o avanço.
  const [errorField, setErrorField] = useState("");

  // Persist selections so returning from payment keeps context.
  useEffect(() => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        shippingAddressId, billingAddressId, deliveryMethod,
        promoCode, voucherAmount, creditsAmount, observations,
      }));
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("MyCheckout: sessionStorage write failed", e);
    }
  }, [shippingAddressId, billingAddressId, deliveryMethod, promoCode, voucherAmount, creditsAmount, observations]);

  // Bootstrap: countries + delivery methods + cart flags (requiresShipping)
  useEffect(() => {
    // Force-refresh do cart context para garantir sincronização entre a
    // expansão do carrinho no menu e os dados desta página (o user pode ter
    // chegado aqui depois de mexer no carrinho noutra página/tab).
    refreshCart?.();
    myApi.shopCountries().then((r) => {
      const arr = r?.items || (Array.isArray(r) ? r : (r?.obj || []));
      setCountries(Array.isArray(arr) ? arr : []);
    }).catch(() => setCountries([]));
    myApi.shopDeliveryMethods().then((r) => {
      const arr = r?.shipmentTypes || r?.obj || r?.items || r;
      setDeliveryMethods(Array.isArray(arr) ? arr : []);
    }).catch(() => setDeliveryMethods([]));
    myApi.shopBalances?.().then?.((r) => {
      setBalances({ vouchers: Number(r?.vouchers || 0), credits: Number(r?.credits || 0) });
    }).catch(() => {});
    myApi.shopCartFlags().then((r) => {
      setCartFlags({
        requiresShipping: !!r?.requiresShipping,
        physicalCount: Number(r?.physicalCount || 0),
        defaultDeliveryMethodId: r?.defaultDeliveryMethodId || "",
        loaded: true,
      });
      // Se não precisa de envio e ainda não há método escolhido, atribuímos
      // automaticamente o default (LE — Levantamento em evento) para que o
      // verify passe sem pedir input ao utilizador.
      if (!r?.requiresShipping && r?.defaultDeliveryMethodId && !deliveryMethod) {
        setDeliveryMethod(r.defaultDeliveryMethodId);
      }
    }).catch(() => setCartFlags((s) => ({ ...s, loaded: true })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced verify: re-run whenever any checkout input changes.
  const verifyDebounced = useDebouncedVerify({
    body: {
      ID_TipoTransporte: deliveryMethod,
      ID_ClienteMoradaEnvio: shippingAddressId,
      PromotionalCode: promoCode,
      Vouchers: voucherAmount,
      Credits: creditsAmount,
      Observacoes: observations,
    },
    enabled: itemCount > 0,
    onStart: () => setVerifying(true),
    onDone: (d) => { setVerifyData(d); setVerifying(false); },
    onError: () => setVerifying(false),
  });

  useEffect(() => { verifyDebounced(); }, [verifyDebounced]);

  // Guard: redirect to cart if empty
  useEffect(() => {
    if (cart !== null && itemCount === 0) {
      // Cart loaded and empty — user shouldn't be here.
      navigate("/my/carrinho", { replace: true });
    }
  }, [cart, itemCount, navigate]);

  const canSubmit = itemCount > 0 && !verifying;

  const handleNext = async () => {
    setSubmitError(""); setErrorField("");
    // Helper interno para exibir e destacar erros — evita "black hole UX"
    // em que o utilizador clica no botão e nada acontece (o erro aparece
    // debaixo do fold, sem toast/scroll). Ver bug reportado por
    // cecilia.reis@zome.pt em Fev/2026.
    const SECTION_TESTID = {
      billing: "billing-address-section",
      shipping: "shipping-address-section",
      delivery: "delivery-method-section",
    };
    const showError = (msg, field) => {
      setSubmitError(msg);
      setErrorField(field || "");
      try {
        setTimeout(() => {
          const el = (field && document.querySelector(`[data-testid="${SECTION_TESTID[field]}"]`))
            || document.querySelector('[data-testid="checkout-submit-error"]')
            || document.querySelector('[data-testid="checkout-totals"]');
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 60);
      } catch (_e) { /* noop */ }
    };
    // Se não há produtos físicos, garantir que o deliveryMethod fica
    // preenchido com o default antes de validar (Levantamento em evento).
    let effectiveDelivery = deliveryMethod;
    if (!cartFlags.requiresShipping && !effectiveDelivery && cartFlags.defaultDeliveryMethodId) {
      effectiveDelivery = cartFlags.defaultDeliveryMethodId;
      setDeliveryMethod(effectiveDelivery);
    }
    // Aplicar exactamente as validações do legacy ANTES de chamar verify/confirm
    const v = validateCheckout({
      verifyData: verifyData?.checkoutData || verifyData,
      billingAddressId, shippingAddressId,
      deliveryMethod: effectiveDelivery, deliveryMethods,
      cartEmpty: itemCount === 0,
      skipPaymentCheck: true,
      requiresShippingOverride: cartFlags.loaded ? cartFlags.requiresShipping : undefined,
    });
    if (!v.ok) { showError(v.message, v.field); return; }

    // Verificação final antes de avançar para pagamento
    setSubmitting(true);
    try {
      const r = await myApi.shopCheckoutVerify({
        ID_TipoTransporte: effectiveDelivery,
        ID_ClienteMoradaEnvio: shippingAddressId,
        PromotionalCode: promoCode,
        Vouchers: voucherAmount,
        Credits: creditsAmount,
        Observacoes: observations,
      });
      const d = r?.checkoutData || r?.obj || r || {};
      if (d.MensagemErro || d.message || r?.success === false) {
        showError(d.MensagemErro || d.message || r.message || "Não foi possível validar a encomenda.");
        return;
      }
      gaEcommerce("add_shipping_info", cartToGaItems(produtos, packs), total, currency,
        { shipping_tier: String(effectiveDelivery || "") });
      navigate("/my/checkout/payment");
    } catch (e) {
      showError(e.message || "Erro ao validar encomenda.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-full bg-[#F9FAFB] py-8 px-4 sm:px-6 lg:px-8" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="my-checkout-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <button type="button" onClick={() => navigate("/my/carrinho")}
            className="text-xs text-[#6B7280] hover:text-[#0F1829] flex items-center gap-1 mb-2 transition-colors">
            <ArrowLeft size={12} /> Voltar ao carrinho
          </button>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0F1829] flex items-center gap-3" style={{ fontFamily: "'Fraunces', sans-serif" }}>
            <ShoppingBag size={30} weight="fill" className="text-[#002480]" />
            Finalizar Encomenda
          </h1>
          <p className="text-[#6B7280] text-sm mt-1">Confirme os dados de envio, faturação e adicione descontos se aplicável.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: form sections */}
          <div className="lg:col-span-2 space-y-4">
            <AddressSection kind="billing" title="Dados de Faturação" countries={countries}
              selectedId={billingAddressId} onSelect={setBillingAddressId}
              invalid={errorField === "billing" ? submitError : ""} />

            {cartFlags.requiresShipping ? (
              <>
                <DeliveryMethodPicker methods={deliveryMethods} selected={deliveryMethod}
                  onSelect={setDeliveryMethod} currency={currency}
                  invalid={errorField === "delivery" ? submitError : ""} />

                <AddressSection kind="shipping" title="Dados de Envio" countries={countries}
                  selectedId={shippingAddressId} onSelect={setShippingAddressId}
                  invalid={errorField === "shipping" ? submitError : ""} />
              </>
            ) : cartFlags.loaded ? (
              <div className="bg-[#F5F5F2] border border-[#E5E7EB] rounded-xl p-4 text-sm text-[#374151]" data-testid="my-checkout-no-shipping">
                <p className="font-semibold text-[#0F1829] mb-1">Sem envio físico</p>
                <p>O seu carrinho contém apenas serviços, cursos ou conteúdos digitais. Não é necessário indicar morada de envio — assumimos <strong>Levantamento em evento</strong> automaticamente.</p>
              </div>
            ) : null}

            <PromoVouchersCredits
              verifyData={verifyData}
              balances={balances}
              promoCode={promoCode} onPromoCodeChange={setPromoCode}
              voucherAmount={voucherAmount} onVoucherChange={setVoucherAmount}
              creditsAmount={creditsAmount} onCreditsChange={setCreditsAmount}
              currency={currency}
              busy={verifying} />

            <ObservationsBox value={observations} onChange={setObservations} />
          </div>

          {/* Right: totals */}
          <CheckoutTotalsCard verifying={verifying} data={verifyData} currency={currency}
            canSubmit={canSubmit} submitting={submitting} submitError={submitError}
            onSubmit={handleNext} />
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function useDebouncedVerify({ body, enabled, onStart, onDone, onError, delay = 400 }) {
  const timerRef = useRef(null);
  const bodyRef = useRef(body);
  bodyRef.current = body;

  return useCallback(() => {
    if (!enabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      onStart?.();
      try {
        const r = await myApi.shopCheckoutVerify(bodyRef.current);
        onDone?.(r);
      } catch (e) {
        onError?.(e);
      }
    }, delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(body), enabled]);
}
